import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { verticalApi, verticalQueryKeys } from "../../shared/api/vertical";
import { apiError } from "../../shared/api/client";
import { vnd } from "../../shared/api/reports";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Đóng gói vertical ngách (#6) — chọn ngành nghề + nạp gói dịch vụ mẫu. */
export function VerticalSettingsPage() {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: verticalQueryKeys.config(),
    queryFn: () => verticalApi.get(),
  });

  const [selected, setSelected] = useState<string>("GENERAL");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cfg.data?.state.vertical) setSelected(cfg.data.state.vertical);
  }, [cfg.data?.state.vertical]);

  const save = useMutation({
    mutationFn: () => verticalApi.set(selected),
    onSuccess: () => {
      setError(null);
      setMsg("Đã lưu ngành nghề.");
      qc.invalidateQueries({ queryKey: verticalQueryKeys.config() });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const apply = useMutation({
    mutationFn: () => verticalApi.applyStarterPack(),
    onSuccess: (r) => {
      setError(null);
      setMsg(
        `Đã nạp gói mẫu: thêm ${r.servicesCreated} dịch vụ` +
          (r.servicesSkipped > 0 ? `, bỏ qua ${r.servicesSkipped} đã có.` : ".")
      );
      qc.invalidateQueries({ queryKey: verticalQueryKeys.config() });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const catalog = cfg.data?.catalog ?? [];
  const current = catalog.find((v) => v.code === selected);

  return (
    <div>
      <PageHeader
        title="Ngành nghề & Gói mẫu"
        description="Chọn loại hình cơ sở để Addy gợi ý sẵn danh mục dịch vụ và cách gọi khách hàng phù hợp. Bạn có thể chỉnh sửa lại sau ở mục Dịch vụ + Giá."
      />

      {cfg.isLoading ? (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      ) : (
        <div className="grid max-w-3xl gap-5">
          <Card>
            <CardContent className="grid gap-3 p-4">
              <div className="space-y-1.5">
                <Label htmlFor="vertical-select">Loại hình cơ sở</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger id="vertical-select" className="max-w-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.map((v) => (
                      <SelectItem key={v.code} value={v.code}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {current && (
                <p className="text-sm text-muted-foreground">
                  Cách gọi khách:{" "}
                  <strong className="text-foreground">{current.customerNoun}</strong>
                </p>
              )}
              <div className="flex gap-2.5">
                <Button
                  type="button"
                  disabled={save.isPending || selected === cfg.data?.state.vertical}
                  onClick={() => save.mutate()}
                >
                  Lưu ngành nghề
                </Button>
              </div>
            </CardContent>
          </Card>

          {current && (
            <Card>
              <CardContent className="p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">
                    Gói dịch vụ mẫu — {current.label}
                  </h3>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={apply.isPending || selected !== cfg.data?.state.vertical}
                    title={
                      selected !== cfg.data?.state.vertical
                        ? "Hãy lưu ngành nghề trước khi nạp gói mẫu."
                        : undefined
                    }
                    onClick={() => {
                      if (
                        confirm(
                          "Nạp các dịch vụ mẫu vào danh mục? Dịch vụ đã tồn tại (theo mã) sẽ được giữ nguyên."
                        )
                      ) {
                        apply.mutate();
                      }
                    }}
                  >
                    {cfg.data?.state.packApplied ? "Nạp lại gói mẫu" : "Nạp gói mẫu"}
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {current.services.map((s) => (
                    <div key={s.code} className="rounded-lg border bg-card p-3">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-semibold text-foreground">{s.name}</span>
                        <span className="text-sm font-medium tabular-nums text-foreground">
                          {vnd(s.priceVnd)}
                        </span>
                      </div>
                      <div className="mt-1 grid gap-0.5 text-sm text-muted-foreground">
                        <div>Mã: {s.code}</div>
                        <div>Nhóm: {s.category}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
          {msg && <p className="text-sm text-emerald-600">{msg}</p>}
        </div>
      )}
    </div>
  );
}
